import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Dumbbell } from "lucide-react";
import { useLang } from "@/i18n";
import { Logo } from "@/components/Logo";

const Navbar = ({ onEnroll }) => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#packages", label: t.nav_packages, testid: "nav-packages-link" },
    { href: "#facilities", label: t.nav_facilities, testid: "nav-facilities-link" },
    { href: "#ai-plan", label: t.nav_aiplan, testid: "nav-aiplan-link" },
    { href: "#equipments", label: t.nav_shop, testid: "nav-shop-link" },
    { href: "#franchise", label: t.nav_franchise, testid: "nav-franchise-link" },
    { href: "#contact", label: t.nav_contact, testid: "nav-contact-link" },
  ];

  const scrollTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
        scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-zinc-800/60" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <a href="#top" onClick={(e) => scrollTo(e, "#top")} data-testid="nav-home-link">
          <Logo />
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => scrollTo(e, l.href)}
              data-testid={l.testid}
              className="font-display text-sm font-semibold uppercase tracking-widest text-zinc-400 transition-colors duration-200 hover:text-volt"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="font-display text-sm font-semibold uppercase tracking-widest text-zinc-600 transition-colors duration-200 hover:text-volt"
          >
            {t.nav_admin}
          </Link>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <div className="flex overflow-hidden rounded-full border border-zinc-700" data-testid="lang-toggle">
            <button
              data-testid="lang-en-button"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
                lang === "en" ? "bg-volt text-ink" : "text-zinc-400 hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-bn-button"
              onClick={() => setLang("bn")}
              className={`px-3 py-1.5 text-xs font-bold transition-colors duration-200 ${
                lang === "bn" ? "bg-volt text-ink" : "text-zinc-400 hover:text-white"
              }`}
            >
              বাং
            </button>
          </div>
          <button
            data-testid="nav-enroll-button"
            onClick={() => onEnroll(null)}
            className="group flex items-center gap-2 rounded-full bg-volt px-5 py-2.5 font-display text-sm font-bold uppercase tracking-widest text-ink transition-all duration-200 hover:bg-volt-hover hover:scale-[1.03]"
          >
            <Dumbbell className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" />
            {t.enroll}
          </button>
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="text-white lg:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-zinc-800 bg-ink/95 px-5 pb-6 pt-2 backdrop-blur-xl lg:hidden" data-testid="mobile-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => scrollTo(e, l.href)}
              data-testid={`mobile-${l.testid}`}
              className="block border-b border-zinc-800/60 py-3.5 font-display text-lg font-semibold uppercase tracking-widest text-zinc-300"
            >
              {l.label}
            </a>
          ))}
          <Link to="/admin" data-testid="mobile-nav-admin-link" className="block border-b border-zinc-800/60 py-3.5 font-display text-lg font-semibold uppercase tracking-widest text-zinc-500">
            {t.nav_admin}
          </Link>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex overflow-hidden rounded-full border border-zinc-700">
              <button data-testid="mobile-lang-en-button" onClick={() => setLang("en")} className={`px-3 py-1.5 text-xs font-bold ${lang === "en" ? "bg-volt text-ink" : "text-zinc-400"}`}>EN</button>
              <button data-testid="mobile-lang-bn-button" onClick={() => setLang("bn")} className={`px-3 py-1.5 text-xs font-bold ${lang === "bn" ? "bg-volt text-ink" : "text-zinc-400"}`}>বাং</button>
            </div>
            <button
              data-testid="mobile-nav-enroll-button"
              onClick={() => { setOpen(false); onEnroll(null); }}
              className="flex-1 rounded-full bg-volt px-5 py-3 font-display text-sm font-bold uppercase tracking-widest text-ink"
            >
              {t.enroll}
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
};

export default Navbar;
